import type { ClientSex } from '../types';

export interface ParsedCnp {
  /** Derived from the leading digit — MALE or FEMALE only (CNP never encodes NOT_STATED). */
  sex: Extract<ClientSex, 'MALE' | 'FEMALE'>;
  /** ISO date (YYYY-MM-DD). */
  dateOfBirth: string;
}

// Romanian CNP layout: S YY MM DD CC NNN K (13 digits).
// The leading digit S encodes both sex and birth century; YY MM DD give the
// birth date. See the national ID specification.
export function parseCnp(cnp: string): ParsedCnp | null {
  if (!/^\d{13}$/.test(cnp)) return null;

  const s = Number(cnp[0]);
  const yy = Number(cnp.slice(1, 3));
  const mm = Number(cnp.slice(3, 5));
  const dd = Number(cnp.slice(5, 7));

  let century: number | null;
  let sex: ParsedCnp['sex'];
  switch (s) {
    case 1: century = 1900; sex = 'MALE'; break;
    case 2: century = 1900; sex = 'FEMALE'; break;
    case 3: century = 1800; sex = 'MALE'; break;
    case 4: century = 1800; sex = 'FEMALE'; break;
    case 5: century = 2000; sex = 'MALE'; break;
    case 6: century = 2000; sex = 'FEMALE'; break;
    // 7/8 — resident foreigners: sex is known, century is not encoded.
    case 7: century = null; sex = 'MALE'; break;
    case 8: century = null; sex = 'FEMALE'; break;
    // 0 / 9 carry no reliable sex or century information.
    default: return null;
  }

  // For resident foreigners (7/8) infer the most recent century that isn't in
  // the future.
  if (century === null) {
    const currentYY = new Date().getFullYear() % 100;
    century = yy <= currentYY ? 2000 : 1900;
  }

  const year = century + yy;

  // Reject impossible dates (e.g. month 13, day 32, Feb 30).
  const date = new Date(Date.UTC(year, mm - 1, dd));
  if (
    date.getUTCFullYear() !== year ||
    date.getUTCMonth() !== mm - 1 ||
    date.getUTCDate() !== dd
  ) {
    return null;
  }

  const dateOfBirth = `${String(year).padStart(4, '0')}-${String(mm).padStart(2, '0')}-${String(dd).padStart(2, '0')}`;
  return { sex, dateOfBirth };
}
